import { useMemo } from 'react';
import { t } from '@lingui/macro';

import dfsSearch from './dfsSearch';
import { appendError } from './formValidation';
import { getObject, setObject } from './mapObject';
import Jnx, { parseJnxExpr } from './jnx';
import parseUISchemaFromSchema from './parseUISchemaFromSchema';
import { compileShowIf, evalShowIf } from '../components/customFields/HideObjectField';
import startsWith from 'lodash/startsWith';
import last from 'lodash/last';

const REQUIRED_JNX = new Jnx('$isEmpty($value) ? "es requerido." : false');

const REQUIRED_IF_JNX = jnx => {
  jnx = parseJnxExpr(jnx);
  jnx.expr = `($isTruthy(${jnx.expr}) and $isEmpty($value)) ? 'es requerido.' : false`;

  return new Jnx(jnx);
};

export default function useJnxFormValidation(schema, uiSchema, formFieldVisibilityRef, context, options) {
  return useMemo(() => {
    const { validations, showIfChecks } = parseValidations(schema, uiSchema, context);

    return validations.length
      ? (formData, errors, scope, formFields, disabledFields) =>
          runJnxValidation(validations, showIfChecks, formData, errors, scope, formFields || {}, disabledFields || {}, options, context)
      : undefined;
  }, [uiSchema, context, options]);
}

// Evaluates every `ui:showIf` gate structurally, straight off the schema/uiSchema tree and current
// formData — independent of which HideObjectField instances happen to be mounted right now. This
// matters because HideObjectField's own `showIfHiddenFields` ref (which this used to read) is only
// ever populated by mount effects, and GroupedTabs/SubformField never mount a tab's content until
// the user actually selects it — so a field inside a never-visited tab would otherwise be treated
// as visible for validation purposes even when its `ui:showIf` would hide it, once
// `ignoreVisibilityOnValidate` is bypassing the "is this mounted" check to validate that tab at all.
//
// A gate found inside a subform (`subformPath` set) is evaluated the same way a live, mounted
// HideObjectField inside that subform would see it: `$` is a "root-shaped" object holding only that
// subform's own data slice (mirroring SubformField's useSubformSideChannel/extractSubFormPublication),
// and `$formObject`/`$formContext.parentRootData` resolve to the outer root form data — because that
// is genuinely what a nested subform's own side channel publishes as those bindings, not the
// top-level loaded form object.
const getIsShowIfHidden = (path, showIfChecks, formData, context) => {
  if (!showIfChecks?.length) return false;

  return showIfChecks.some(({ path: checkPath, jnx, subformPath }) => {
    const checkPathStr = checkPath.toString();
    if (path !== checkPathStr && !path.startsWith(`${checkPathStr}.`)) return false;

    if (!subformPath) {
      const bindings = {
        formContext: context?.formContext,
        formObject: context?.formObject,
        rootFormData: formData,
      };

      return !evalShowIf(formData, checkPathStr, jnx, bindings);
    }

    const subformPathStr = subformPath.toString();
    const subformSlice = getObject(formData, subformPathStr);
    const rootSubformData = {};
    setObject(rootSubformData, subformPathStr, subformSlice);

    const bindings = {
      formContext: { ...context?.formContext, parentRootData: formData },
      formObject: formData,
      rootFormData: formData,
    };

    return !evalShowIf(rootSubformData, checkPathStr, jnx, bindings);
  });
};

const getIsDisabled = (path, disabledFields, context) => {
  if (disabledFields[path] != null) return disabledFields[path];

  // check objMap
  const { objectMapAsMap = {}, invObjectMapAsMap = {} } = context?.formContext?.formDefinition ?? {};
  const objMapKey = objectMapAsMap[path];
  if (objMapKey != null) return objMapKey;

  // check if subform field
  const target = Object.keys(invObjectMapAsMap).find(key => startsWith(path, key));

  if (target) {
    const maybeSubformName = last(target.split('.'));

    if (maybeSubformName && context.subforms[maybeSubformName]) {
      const subform = context.subforms[maybeSubformName];
      const { objectMapAsMap: _objectMapAsMap = {} } = subform.formDef ?? {};
      const directPath = `${path.replace(target + '.', '')}`;
      const mappedPath = _objectMapAsMap[directPath];

      if (mappedPath) {
        return disabledFields[`${target}.${mappedPath}`];
      }
    }
  }

  return false;
};

function runJnxValidation(validations, showIfChecks, formData, errors, _, formFields, disabledFields, options = {}, context) {
  validations.forEach(({ path: acPath, jnx, translatedPath: acTranslatedPath, requireAll }) => {
    // acPath walks the UI-mounted structure (its resolved `path` strings are what formFields/
    // disabledFields are keyed by), so it must always drive iteration/gating. acTranslatedPath,
    // when present, points at the real formData location for a subform-mapped field (e.g.
    // "tabGroups.ND-BalanceSheet.data.X" vs. the mounted "tabGroups.ND-BalanceSheet.X"), so it's
    // only used to fetch the actual value/path handed to jnx.eval.
    const translatedList = acTranslatedPath?.getAll(formData);

    acPath.getAll(formData).forEach(([path, pathValue], index) => {
      let isVisible = formFields[path];

      const isDisabledOrReadonly = requireAll && !!getIsDisabled(path, disabledFields, context);
      const isShowIfHidden = getIsShowIfHidden(path, showIfChecks, formData, context);
      const [evalPath, evalValue] = acTranslatedPath ? translatedList[index] ?? [path, pathValue] : [path, pathValue];
      let result = null;

      try {
        result =
          (isVisible || options?.ignoreVisibility) && !isDisabledOrReadonly && !isShowIfHidden
            ? jnx.eval(formData, evalPath, {
                value: evalValue,
                rootFormData: formData,
              })
            : false;
      } catch (e) {
        appendError(errors, acPath.toString(), t`Error evaluating requirement`);
      }

      if (result) {
        appendError(errors, acPath.toString(), result);
      }
    });
  });

  return errors;
}

/** Represents an object path that is aware of arrays in the path and can be used to list all reachable objects.
 */
class ArrayConsciousObjectPath {
  constructor(segments) {
    this.segments = segments || [];
  }

  append(component) {
    const segments = this.segments.slice();
    const last = segments[segments.length - 1] || '';
    const separator = last.length ? '.' : '';
    const current = `${last}${separator}${component}`;

    if (!segments.length) {
      segments.push(current);
    } else {
      segments[segments.length - 1] = current;
    }

    return new ArrayConsciousObjectPath(segments);
  }

  appendArray() {
    return new ArrayConsciousObjectPath(this.segments.concat(''));
  }

  getAll(data) {
    const list = [];
    const N = this.segments.length;
    const N1 = N - 1;
    dfsSearch([['', 0, data]], ([parentPath, segmentIdx, searchData]) => {
      const segment = this.segments[segmentIdx];
      let segmentPath = `${parentPath}${parentPath.length ? '.' : ''}${segment}`;
      const segmentData = getObject(searchData, segment);

      if (segmentData && Array.isArray(segmentData) && segmentIdx < N1) {
        const nextIdx = segmentIdx + 1;
        const segmentSep = segmentPath.length ? '.' : '';

        return segmentData.map((item, idx) => [`${segmentPath}${segmentSep}${idx}`, nextIdx, item]);
      } else if (segmentIdx === N1) {
        if (segmentPath.endsWith('.')) segmentPath = segmentPath.substring(0, segmentPath.length - 1);
        list.push([segmentPath, segmentData]);
      }
    });

    return list;
  }

  clone() {
    return new ArrayConsciousObjectPath(this.segments);
  }

  toString() {
    return this.segments.join('.');
  }
}

/** Parses the validation expressions in the given schema and uiSchema pair.
 * @param {object} schema - the jsonschema to parse
 * @param {object} uiSchema - the associated ui schema to parse
 * @param {object} context - extra data/context for the current form, currently used for subforms inside main form
 * @returns {{validations: array, showIfChecks: array}}
 */
function parseValidations(schema, uiSchema, context) {
  const validations = [];
  const showIfChecks = [];

  dfsSearch(
    [[new ArrayConsciousObjectPath(['']), '', schema, uiSchema, undefined, false]],
    ([currentPath, parentName, currentSchema, currentUISchema, subformPath, inheritedRequireAll]) => {
      if (!currentUISchema) return;

      const { type, properties, items, title } = currentSchema;
      const name = (title || parentName).replaceAll('*', '').trim();
      const requireAllChildren = inheritedRequireAll || currentUISchema['akc:requireAllChildren'] === true;

      // Checked before the SubformField branch's early return below (which recurses into the
      // subform's own properties and never reaches the rest of this function for the SubformField
      // node itself) — a `ui:showIf` on the SubformField property (gating the whole tab/subform,
      // as opposed to `ui:staticShowIf`) is just as real a HideObjectField exclusion as one on any
      // other child, and must not be skipped just because this node also happens to recurse.
      if (currentUISchema['ui:showIf']) {
        tryAddShowIfCheck(showIfChecks, currentPath, currentUISchema['ui:showIf'], subformPath);
      }

      if (currentSchema['ui:field'] === 'SubformField' && currentSchema['ui:subformName']) {
        const subform = context.subforms[currentSchema['ui:subformName']];

        if (subform) {
          const sfSchema = {
            type: 'object',
            properties: subform.schema,
          };
          const sfUiSchema = parseUISchemaFromSchema(sfSchema, subform.uiSchema);
          const pathToSubform = currentPath.clone();

          return Object.entries(sfSchema.properties).map(([propName, propSchema]) => {
            const _name = (propSchema.title || name || parentName).replaceAll('*', '').trim();

            return [currentPath.append(propName), _name, propSchema, sfUiSchema[propName], pathToSubform, requireAllChildren];
          });
        }
      }

      const { 'akc:requiredIfVisible': requiredIfVisible, 'akc:requiredIf': requiredIfExpr, 'akc:validate': validateExpr } = currentUISchema;

      const translatedPath = subformPath ? subformPath.append(currentSchema['map:field'] ?? name) : currentPath;

      if (requiredIfVisible) {
        tryAddValidation(validations, currentPath, () => REQUIRED_JNX, translatedPath);
      }

      if (requiredIfExpr) {
        tryAddValidation(validations, currentPath, () => REQUIRED_IF_JNX(requiredIfExpr), translatedPath);
      }

      if (validateExpr) {
        tryAddValidation(validations, currentPath, () => new Jnx(validateExpr), translatedPath);
      }

      const isContainer = type === 'object' || type === 'array';

      // requiredIfVisible !== false excludes only the explicit opt-out (akc:requiredIfVisible: false);
      // StaticText fields are presentational. ComputedFields are required like any other field: a
      // value that never got computed (a tab nobody opened after a bulk upload) must block Submit,
      // not reach analytics missing. Formulas return 0, not null, on blank or zero inputs; a
      // computed field that can legitimately be empty (a derived date) opts out.
      if (
        requireAllChildren &&
        !isContainer &&
        requiredIfVisible !== false &&
        'map:field' in currentSchema &&
        currentUISchema['ui:staticText'] !== true
      ) {
        tryAddValidation(validations, currentPath, () => REQUIRED_JNX, translatedPath, true);
      }

      switch (type) {
        case 'object': {
          return Object.entries(properties).map(([propName, propSchema]) => [
            currentPath.append(propName),
            name,
            propSchema,
            currentUISchema[propName],
            subformPath,
            requireAllChildren,
          ]);
        }

        case 'array': {
          return [[currentPath.appendArray(), name, items, currentUISchema.items, subformPath, requireAllChildren]];
        }

        default:
          break;
      }
    }
  );

  return { validations, showIfChecks };
}

function tryAddShowIfCheck(list, path, showIfExpr, subformPath) {
  try {
    list.push({ path, jnx: compileShowIf(showIfExpr), subformPath });
  } catch (e) {
    console.error('Error parsing ui:showIf expression', e);
  }
}

function tryAddValidation(list, path, jnxFn, translatedPath, requireAll) {
  try {
    const jnx = jnxFn();
    list.push({ path, jnx, translatedPath, requireAll });
  } catch (e) {
    console.error('Error parsing jnx validation', e);
  }
}
